import type { ReactNode } from 'react';
import { useSpMessageLaunch } from '@/features/messaging/hooks/useSpMessageLaunch';

type CareTeamMessageGateProps = {
  providerId: string;
  providerName: string;
  sourceSurface: string;
  /** True when messaging should be gated behind the guest signup modal instead. */
  isGuest?: boolean;
  onGuestGate?: () => void;
  /** Render the actual CTA(s) — receives the click handler to wire onto them. */
  children: (onMessage: () => void) => ReactNode;
};

/**
 * "Message this provider" gate for a care-plan/Navigator provider row. Wraps
 * the shared launch gate (ticket 07) as a render-prop component — extracted
 * because a care plan renders several provider rows, and each needs its own
 * hook instance keyed to that provider's id, while callers keep full control
 * of their own button markup/styling.
 */
export const CareTeamMessageGate = ({
  providerId,
  providerName,
  sourceSurface,
  isGuest = false,
  onGuestGate,
  children,
}: CareTeamMessageGateProps) => {
  const { launch, modal } = useSpMessageLaunch({
    serviceProviderId: providerId,
    providerName,
    sourceSurface,
  });

  const onMessage = () => {
    if (isGuest) {
      onGuestGate?.();
      return;
    }
    void launch();
  };

  return (
    <>
      {children(onMessage)}
      {modal}
    </>
  );
};
